import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  type ClassDetailsForm,
  useCreateClassMutation,
} from '~entities/classes/api/create-class';
import { getClassQueryOptions } from '~entities/classes/api/get-class';
import { getHourlyTuitionBillingId } from '~entities/classes/helpers/get-hourly-tuition-billing-id';
import type { ClassDetails } from '~entities/classes/modal/class';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

function buildClassDetailsFormFromClass(
  c: ClassDetails,
  title: string,
): ClassDetailsForm {
  return {
    title,
    description: c.description ?? '',
    location: c.location?._id ?? null,
    room: c.room?._id ?? null,
    instructor: c.instructor?.map((i) => i._id) ?? [],
    tags: c.tags?.map((t) => t._id) ?? [],
    group: c.group?.map((g) => g._id) ?? [],
    hide: c.hide,
    color: c.color,
    startDate: c.startDate,
    endDate: c.endDate,
    startTime: c.startTime,
    endTime: c.endTime,
    days: c.days,
    duration: c.duration,
    isPrivateLesson: c.isPrivateLesson ?? false,
    slotInterval: c.slotInterval,
    session: c.session._id,
    startYear: c.startYear,
    startMonth: c.startMonth,
    endYear: c.endYear,
    endMonth: c.endMonth,
    maxSize: c.maxSize,
    policyGroup: c.policyGroup?.map((p) => p._id) ?? [],
    registrationStartDate: c.registrationStartDate ?? '',
    registrationFeeAmount: c.registrationFeeAmount,
    prorateTuition: c.prorateTuition,
    tuitionFee: c.tuitionFee,
    costumeFee: c.costumeFee ?? 0,
    tuitionBillingCycle: c.tuitionBillingCycle,
    billingFrequencyDays: c.billingFrequencyDays,
    hourlyTuitionBillingId: getHourlyTuitionBillingId(c.hourlyTuitionBillingId),
    billingDay: c.billingDay,
    availability: c.availability,
    timeConfig: c.timeConfig,
    displayOnWebsite: c.displayOnWebsite,
    allowOnlineRegistration: c.allowOnlineRegistration,
    allowPortalEnrollment: c.allowPortalEnrollment,
    allowTrialEnrollment: c.allowTrialEnrollment,
    includeInPublicSchedule: c.includeInPublicSchedule ?? false,
  };
}

const defaultCopyTitle = (title: string) => `${title} (Copy)`;

export interface CopyClassButtonProps {
  classId: string;
  children?: (props: {
    openDialog: () => void;
    isPending: boolean;
  }) => React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CopyClassButton({
  classId,
  children,
  open,
  onOpenChange,
}: CopyClassButtonProps) {
  const navigate = useNavigate();
  const { mutateAsync: copyClass, isPending } = useCreateClassMutation();
  const { data: classData, isLoading: isClassLoading } = useQuery({
    ...getClassQueryOptions(classId),
    enabled: !!classId,
  });

  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const dialogOpen = isControlled ? open : uncontrolledOpen;
  const [newClassName, setNewClassName] = useState('');

  const setDialogOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  useEffect(() => {
    if (dialogOpen && classData) {
      setNewClassName(defaultCopyTitle(classData.title));
    }
  }, [dialogOpen, classData]);

  const openDialog = () => {
    setDialogOpen(true);
  };

  const handleCopyConfirm = async () => {
    if (!classData) return;
    const name = newClassName.trim();
    if (!name) {
      toast.error('Please enter a name for the new class.');
      return;
    }
    const imageUrl = classData.imageUrl.trim();
    if (!imageUrl) {
      toast.error('Cannot copy class: no cover image.');
      setDialogOpen(false);
      return;
    }
    try {
      const classDetails = buildClassDetailsFormFromClass(classData, name);
      await copyClass({
        file: imageUrl,
        fileType: 'url',
        classDetails,
      });
      setDialogOpen(false);
      toast.success('Class copied successfully.');
      void navigate({ to: '/classes' });
    } catch {
      toast.error('Failed to copy class. Please try again.');
    }
  };

  return (
    <>
      {children?.({ openDialog, isPending })}

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Copy class</AlertDialogTitle>
            <AlertDialogDescription>
              {isClassLoading ? (
                'Loading class details…'
              ) : (
                <>
                  This will create a new class with the same settings as &quot;
                  {classData?.title}&quot;. The new class will have no enrolled
                  students.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {classData && (
            <>
              <div className='grid gap-2 py-2'>
                <Label htmlFor='copy-class-name'>New class name</Label>
                <Input
                  id='copy-class-name'
                  value={newClassName}
                  onChange={(e) => {
                    setNewClassName(e.target.value);
                  }}
                  placeholder='Class name (Copy)'
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void handleCopyConfirm();
                  }}
                  disabled={isPending}
                >
                  {isPending && <Loader2 className='animate-spin' />}
                  Copy class
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
